import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RevenueCalculator = ({ pricingTiers }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [currentSubscribers, setCurrentSubscribers] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [churnRate, setChurnRate] = useState('5');

  const calculateImpact = () => {
    if (!selectedPlan || !currentSubscribers || !newPrice) return null;

    const plan = pricingTiers?.find(t => t?.id === selectedPlan);
    if (!plan) return null;

    const currentRevenue = plan?.price * parseInt(currentSubscribers);
    const projectedRevenue = parseFloat(newPrice) * parseInt(currentSubscribers) * (1 - parseFloat(churnRate) / 100);
    const difference = projectedRevenue - currentRevenue;
    const percentChange = ((difference / currentRevenue) * 100)?.toFixed(1);

    return {
      currentRevenue,
      projectedRevenue,
      difference,
      percentChange,
      retainedSubscribers: Math.floor(parseInt(currentSubscribers) * (1 - parseFloat(churnRate) / 100))
    };
  };

  const impact = calculateImpact();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calculator Form */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Revenue Impact Calculator</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Project the financial impact of pricing changes
        </p>

        <div className="space-y-4">
          <Select
            label="Select Plan"
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e?.target?.value)}
            options={[
              { value: '', label: 'Choose a plan...' },
              ...pricingTiers?.map(tier => ({
                value: tier?.id,
                label: `${tier?.tierName} - ${formatCurrency(tier?.price)}/${tier?.billingPeriod}`
              }))
            ]}
          />

          <Input
            label="Current Subscribers"
            type="number"
            value={currentSubscribers}
            onChange={(e) => setCurrentSubscribers(e?.target?.value)}
            placeholder="e.g., 150"
          />

          <Input
            label="New Price"
            type="number"
            value={newPrice}
            onChange={(e) => setNewPrice(e?.target?.value)}
            placeholder="e.g., 49"
          />

          <Input
            label="Expected Churn Rate (%)"
            type="number"
            value={churnRate}
            onChange={(e) => setChurnRate(e?.target?.value)}
            placeholder="e.g., 5"
          />
        </div>
      </div>
      {/* Results */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Projected Impact</h3>
        
        {impact ? (
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Current Monthly Revenue</p>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(impact?.currentRevenue)}
              </p>
            </div>

            <div className="p-4 bg-primary/10 rounded-lg border-2 border-primary">
              <p className="text-sm text-muted-foreground mb-1">Projected Monthly Revenue</p>
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(impact?.projectedRevenue)}
              </p>
            </div>

            <div className={`p-4 rounded-lg ${
              impact?.difference >= 0 ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
            }`}>
              <p className="text-sm text-muted-foreground mb-1">Revenue Change</p>
              <div className="flex items-center gap-2">
                <p className={`text-2xl font-bold ${
                  impact?.difference >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {impact?.difference >= 0 ? '+' : ''}{formatCurrency(impact?.difference)}
                </p>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  impact?.difference >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {impact?.percentChange}%
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Retained Subscribers</p>
                <p className="text-lg font-semibold text-foreground">
                  {impact?.retainedSubscribers}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Expected Churn</p>
                <p className="text-lg font-semibold text-foreground">
                  {parseInt(currentSubscribers) - impact?.retainedSubscribers}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  This calculation assumes a {churnRate}% churn rate. Actual results may vary based on market conditions, customer satisfaction, and competitive landscape.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="Calculator" size={48} className="text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Fill in the calculator form to see projected revenue impact
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueCalculator;
